import React, { useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import { useBulkUpsertFieldValues } from "../../api/mutations/field-values/useBulkFieldValues";
import { useTagList } from "../../api/queries/tag/list";
import { ProfileFieldValue } from "../../api/types/crm/profile-fields";

type ChildTag = {
  id: string;
  label: string;
  fieldValueIds: string[];
};

interface TagChipsProps {
  childTags: ChildTag[];
  existingFieldValues: ProfileFieldValue[];
  profileId: string;
  parentFieldValueId: string | null | undefined;
  onSaveSuccess?: () => void;
  onSaveError?: (error: string) => void;
}

export const TagChips: React.FC<TagChipsProps> = ({
  childTags,
  existingFieldValues,
  profileId,
  parentFieldValueId,
  onSaveSuccess,
  onSaveError,
}) => {
  const bulkUpsertMutation = useBulkUpsertFieldValues();
  const { data: tagListResponse } = useTagList();
  const overlayPanelRef = useRef<OverlayPanel>(null);
  const [editingTag, setEditingTag] = useState<{
    tagFieldValueIds: string[];
    currentTagId: string;
    parentFieldValueId: string;
    anchorElement: HTMLElement | null;
  } | null>(null);

  const tagOptions = useMemo(
    () =>
      tagListResponse?.data?.results?.map((tag) => ({
        label: tag.name,
        value: tag.id,
      })) ?? [],
    [tagListResponse?.data?.results],
  );

  if (!childTags || childTags.length === 0 || !parentFieldValueId) {
    return null;
  }

  const handleTagClick = (
    tagFieldValueIds: string[],
    tagId: string,
    event: React.MouseEvent<HTMLSpanElement>,
  ) => {
    setEditingTag({
      tagFieldValueIds,
      currentTagId: tagId,
      parentFieldValueId,
      anchorElement: event.currentTarget,
    });
    overlayPanelRef.current?.show(event, event.currentTarget);
  };

  const handleReplaceTag = async (newTagId: string) => {
    if (!editingTag) return;

    const tagFieldValues = existingFieldValues.filter((fv) =>
      editingTag.tagFieldValueIds.includes(fv.id),
    );

    if (tagFieldValues.length === 0) {
      overlayPanelRef.current?.hide();
      setEditingTag(null);
      return;
    }

    const newTag = tagListResponse?.data?.results?.find(
      (t) => t.id === newTagId,
    );
    if (!newTag) return;

    const fieldsToUpdate = tagFieldValues.map((tagFieldValue) => {
      let currentTags = tagFieldValue.value as
        | Array<{ id: string; label: string }>
        | string
        | null
        | undefined;

      if (typeof currentTags === "string") {
        try {
          currentTags = JSON.parse(currentTags);
        } catch (error) {
          currentTags = [];
        }
      }

      if (!Array.isArray(currentTags)) {
        currentTags = [];
      }

      const updatedTags = currentTags.map((tag) =>
        tag.id === editingTag.currentTagId
          ? { id: newTag.id, label: newTag.name }
          : tag,
      );

      const uniqueTags = updatedTags.filter(
        (tag, index, self) => index === self.findIndex((t) => t.id === tag.id),
      );

      return {
        field_type_id: tagFieldValue.fieldTypeId,
        value: uniqueTags.map((t) => t.id),
        parent_id: editingTag.parentFieldValueId,
        sort_order: tagFieldValue.sortOrder,
      };
    });

    const uniqueFieldsToUpdate = fieldsToUpdate.filter(
      (field, index, self) =>
        index ===
        self.findIndex(
          (f) =>
            f.field_type_id === field.field_type_id &&
            f.parent_id === field.parent_id &&
            f.sort_order === field.sort_order,
        ),
    );

    try {
      await bulkUpsertMutation.mutateAsync({
        profile_id: profileId,
        fields: uniqueFieldsToUpdate,
        // Replace to avoid backend ambiguity when duplicates exist for the same parent/sort
        replace_mode: true,
      });

      onSaveSuccess?.();
      overlayPanelRef.current?.hide();
      setEditingTag(null);
    } catch (error: any) {
      onSaveError?.(error?.message || "Failed to update tag");
    }
  };

  const handleRemoveTag = async () => {
    if (!editingTag) return;

    const tagFieldValues = existingFieldValues.filter((fv) =>
      editingTag.tagFieldValueIds.includes(fv.id),
    );

    if (tagFieldValues.length === 0) {
      overlayPanelRef.current?.hide();
      setEditingTag(null);
      return;
    }

    const fieldsToUpdate = tagFieldValues.map((tagFieldValue) => {
      let currentTags = tagFieldValue.value as
        | Array<{ id: string; label: string }>
        | string
        | null
        | undefined;

      if (typeof currentTags === "string") {
        try {
          currentTags = JSON.parse(currentTags);
        } catch (error) {
          currentTags = [];
        }
      }

      if (!Array.isArray(currentTags)) {
        currentTags = [];
      }

      const updatedTags = currentTags.filter(
        (tag) => tag.id !== editingTag.currentTagId,
      );

      return {
        field_type_id: tagFieldValue.fieldTypeId,
        value: updatedTags.map((t) => t.id),
        parent_id: editingTag.parentFieldValueId,
        sort_order: tagFieldValue.sortOrder,
      };
    });

    try {
      await bulkUpsertMutation.mutateAsync({
        profile_id: profileId,
        fields: fieldsToUpdate,
        // Replace to avoid backend ambiguity when duplicates exist for the same parent/sort
        replace_mode: true,
      });

      onSaveSuccess?.();
      overlayPanelRef.current?.hide();
      setEditingTag(null);
    } catch (error: any) {
      onSaveError?.(error?.message || "Failed to remove tag");
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-1 mt-1">
        {childTags.map((tag) => (
          <span
            key={tag.id}
            className="px-2 py-1 border-round text-xs cursor-pointer"
            style={{
              backgroundColor: "#e0e7ff",
              color: "#3730a3",
              transition: "all 0.2s",
            }}
            onClick={(e) => handleTagClick(tag.fieldValueIds, tag.id, e)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c7d2fe";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#e0e7ff";
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleTagClick(tag.fieldValueIds, tag.id, e as any);
              }
            }}
            aria-label={`Edit tag ${tag.label}`}
          >
            <i className="pi pi-tag mr-1" style={{ fontSize: "0.7rem" }} />
            {tag.label}
            <i
              className="pi pi-pencil ml-1"
              style={{ fontSize: "0.6rem", opacity: 0.7 }}
            />
          </span>
        ))}
      </div>

      <OverlayPanel
        ref={overlayPanelRef}
        onHide={() => setEditingTag(null)}
        dismissable
        showCloseIcon
        style={{ width: "300px" }}
      >
        <div className="flex flex-column gap-2">
          <label className="font-semibold text-sm">Replace Tag</label>
          <Dropdown
            value={editingTag?.currentTagId}
            options={tagOptions.filter(
              (opt) => opt.value !== editingTag?.currentTagId,
            )}
            onChange={(e) => handleReplaceTag(e.value)}
            placeholder="Select a tag"
            filter
            autoFocus
            className="w-full"
            disabled={bulkUpsertMutation.isLoading}
          />
          <div className="flex gap-2 mt-2">
            <Button
              label="Remove Tag"
              icon="pi pi-trash"
              severity="danger"
              size="small"
              outlined
              onClick={handleRemoveTag}
              disabled={bulkUpsertMutation.isLoading}
              className="flex-1"
            />
            <Button
              label="Cancel"
              severity="secondary"
              size="small"
              outlined
              onClick={() => {
                overlayPanelRef.current?.hide();
                setEditingTag(null);
              }}
              disabled={bulkUpsertMutation.isLoading}
              className="flex-1"
            />
          </div>
        </div>
      </OverlayPanel>
    </>
  );
};
